import { useState, useEffect } from "react";
import { Badge, Card } from "@weretradeit/ui-react";

interface ErrorLogEntry {
  id: string;
  projectName: string;
  severity: "error" | "warn" | "info";
  message: string;
  environment: string;
  status: "open" | "in-progress" | "closed";
  context?: {
    requestId?: string;
  };
}

const severityColor = (severity: ErrorLogEntry["severity"]) => {
  switch (severity) {
    case "error":
      return "#ef476f";
    case "warn":
      return "#ffd166";
    default:
      return "#3ea6ff";
  }
};

export const GeneralLogsWidget = () => {
  const [logs, setLogs] = useState<ErrorLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();

  useEffect(() => {
    const fetchLogs = async () => {
      setLoading(true);
      setError(undefined);
      try {
        const response = await fetch("/logging/api/errors", {
          credentials: "include"
        });
        if (!response.ok) {
          throw new Error(`Request failed with ${response.status}`);
        }
        const payload = await response.json();
        const data = payload.data ?? payload;
        // Show only the 5 most recent logs
        setLogs(Array.isArray(data) ? data.slice(0, 5) : []);
      } catch (err) {
        setError((err as Error).message);
      } finally {
        setLoading(false);
      }
    };

    fetchLogs();
    // Refresh every 30 seconds
    const interval = setInterval(fetchLogs, 30000);
    return () => clearInterval(interval);
  }, []);

  return (
    <Card title="General Logs" className="log-widget">
      <p style={{ marginTop: 0, marginBottom: 12 }}>Recent error and warning events from all services.</p>
      {loading && <p>Loading logs...</p>}
      {error && <Badge tone="warn">Failed to load: {error}</Badge>}
      {!loading && !error && logs.length === 0 && <p>No recent logs.</p>}
      {!loading && !error && logs.length > 0 && (
        <table className="token-grid logs-table" style={{ fontSize: "0.9em" }}>
          <thead>
            <tr>
              <th>Project</th>
              <th>Severity</th>
              <th>Message</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {logs.map((entry) => (
              <tr key={entry.id}>
                <td>{entry.projectName}</td>
                <td style={{ color: severityColor(entry.severity), fontWeight: 600 }}>
                  {entry.severity}
                </td>
                <td style={{ maxWidth: "300px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {entry.message}
                </td>
                <td>
                  <Badge tone={entry.status === "closed" ? "success" : entry.status === "open" ? "warn" : "info"}>
                    {entry.status}
                  </Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div style={{ marginTop: 12, textAlign: "right" }}>
        <a href="/logging" style={{ fontSize: "0.9em", color: "var(--accent)" }}>View all logs →</a>
      </div>
    </Card>
  );
};
